<!-- eslint-disable vue/no-mutating-props -->
<template>
  <v-card
    id="highlights"
    :border="isSpecialist"
    tile
  >
    <v-card-title class="tab-title d-flex">
      <StandardButton
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('Highlights') }}
      </h2>
    </v-card-title>
    <v-divider />
    <v-card-text class="my-4">
      <v-row>
        <LabeledAutocomplete
          v-for="(key, index) in keys"
          @change="processHighlightsUpdates"
          @update:model-value="(v) => setHighlight(key, v)"
          :key="index"
          :data-cy="'highlights-' + key"
          :field="'highlights.properties.' + key"
          :model-value="normalizedHighlightValue(key)"
          :multiple="isMultiSelect(key)"
          :schema-id="provider.schema_id"
          chips
          closable-chips
          global
        />
      </v-row>
      <v-row>
        <LabeledTextarea
          v-model="provider.other_programs_description"
          id="manager_include_other_highlights_info"
          :hard-lock="otherProgramsDescriptionDisabled()"
          :message="otherProgramsDescriptionTitle()"
          :schema-id="provider.schema_id"
          aria-label="Add any information you want to highlight"
          field="other_programs_description"
          global
          tracked
        />
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { useStore } from 'vuex';

import LabeledAutocomplete from '@/shared/components/form/LabeledAutocomplete.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import useRole from '@/shared/composables/useRole';

const $bct = inject('translator');

const store = useStore();
const { isSpecialist, role } = useRole();

const props = defineProps({
  provider: {
    type: Object,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const emit = defineEmits(['progress', 'toggleProviderProfileNav']);

const keys = Object.keys(schemaProperties().highlights.properties);

function load() {
  calculateProgress();
}

function otherProgramsDescriptionDisabled() {
  return !schemaProperties().other_programs_description.editRoles?.includes(role);
}

function otherProgramsDescriptionTitle() {
  return schemaProperties().other_programs_description.alias || 'Other program information:';
}

function calculateProgress() {
  const hasValue = (key) => {
    const v = props.provider.highlights?.[key];
    if (v == null) return false;
    return Array.isArray(v) ? v.length > 0 : String(v).length > 0;
  };
  const progress = keys.some(hasValue) ? 100 : 0;
  emit('progress', progress);
}

function processHighlightsUpdates() {
  calculateProgress();
}

function schemaProperties() {
  return store.state.schemas[props.provider.schema_id].definition.properties;
}

function propertySchema(key) {
  return schemaProperties().highlights?.properties?.[key] || {};
}

function isMultiSelect(key) {
  return propertySchema(key).type !== 'string';
}

function normalizedHighlightValue(key) {
  const highlightValue = props.provider.highlights?.[key];
  const isMultiple = isMultiSelect(key);

  if (highlightValue == null || highlightValue === '') {
    return isMultiple ? [] : null;
  }

  if (isMultiple) {
    const asArray = Array.isArray(highlightValue) ? highlightValue : [highlightValue];
    return asArray;
  }

  const asString = Array.isArray(highlightValue) ? (highlightValue[0] ?? null) : highlightValue;
  return asString === '' ? null : asString;
}

function setHighlight(key, value) {
  if (!props.provider.highlights) return;
  if (isMultiSelect(key)) {
    props.provider.highlights[key] = Array.isArray(value) ? value : value != null ? [value] : [];
  } else {
    props.provider.highlights[key] = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
  }
}

onMounted(load);
</script>
